import { useCallback, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { OperatorState, PresentationDetail } from '../../shared/api/types';
import { Alert, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { presentationFileUrl } from './SlideView';
import { LiveMonitor, QUICK } from './ConsolePage';
import { usePolling } from './usePolling';
import { usePresentationSocket } from './usePresentationSocket';
import { AssistPanel } from './RemoteHelp';
import { CannotOperate } from './Lapsed';
import { isVideoSlide, PlaybackBar } from './Player';

type Command = Record<string, unknown> & { action: string };

/**
 * Control desde el celular: botones grandes para avanzar, Logo, Negro y el programa para saltar.
 * Un toque en una diapositiva la pone al aire de inmediato (no hay «Siguiente» aparte: es un control remoto).
 */
export function RemotePage() {
  const { presentationId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/presentation/churches/${churchId}/presentations/${presentationId}`;
  const refresh = useRef(() => {});
  const instant = usePresentationSocket(churchId ? { churchId, presentationId } : null, () => refresh.current());
  const state = usePolling((signal) => api.get<OperatorState>(`${base}/live`, signal), instant ? 5000 : 1500, [base]);
  refresh.current = state.refresh;
  const detail = useAsync((signal) => (churchId ? api.get<PresentationDetail>(base, signal) : Promise.reject(new Error('Sin contexto'))), [base]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (command: Command) => {
      setBusy(command.action);
      setError(null);
      try {
        state.setData(await api.post<OperatorState>(`${base}/control`, command));
        navigator.vibrate?.(15);
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [base, state],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (!me.can('presentation.operate')) return <CannotOperate churchId={churchId} fallback="Solo quien opera presentaciones puede usar el control." />;
  const s = state.data;
  if (!s) return state.error ? <ErrorState error={state.error} onRetry={state.refresh} /> : <Loading />;
  const p = detail.status === 'ready' ? detail.data : null;
  const fileUrl = (rid: string) => presentationFileUrl(churchId, presentationId, rid);

  return (
    <div className="page remote">
      <p className="small">
        <Link to={`/app/presentaciones/${presentationId}`}>← Consola completa</Link>
      </p>
      <h1 className="remote__title">{p?.title ?? s.title}</h1>
      <AssistPanel churchId={churchId} presentationId={presentationId} />
      {error ? <Alert>{error}</Alert> : null}
      {state.error ? <Alert tone="info">Sin conexión. Reintentando…</Alert> : null}
      <figure className="monitor monitor--live">
        <figcaption>
          En pantalla {s.blackout ? <span className="badge badge--warn">Negro</span> : null}
          {s.logo && !s.blackout ? <span className="badge">Logo</span> : null}
        </figcaption>
        <LiveMonitor state={s} fileUrl={fileUrl} />
        {isVideoSlide(s.live) && !s.blackout && !s.logo ? <PlaybackBar playback={s.playback} serverTime={s.serverTime} info={null} send={(c) => void send(c)} compact /> : null}
      </figure>
      <div className="remote__nav">
        <button type="button" className="remote__big" disabled={busy !== null} onClick={() => void send({ action: 'previous' })}>
          ◀<span>Anterior</span>
        </button>
        <button type="button" className="remote__big remote__big--next" disabled={busy !== null} onClick={() => void send({ action: s.live ? 'next' : 'take' })}>
          <span>{s.live ? 'Siguiente' : 'Empezar'}</span>▶
        </button>
      </div>
      <div className="remote__toggles">
        <button type="button" className={`remote__toggle${s.logo && !s.blackout ? ' remote__toggle--on' : ''}`} aria-pressed={Boolean(s.logo)} onClick={() => void send({ action: 'logo', on: !s.logo })}>
          Logo
        </button>
        <button type="button" className={`remote__toggle${s.blackout ? ' remote__toggle--black' : ''}`} aria-pressed={s.blackout} onClick={() => void send({ action: 'blackout', on: !s.blackout })}>
          Negro
        </button>
        <button type="button" className={`remote__toggle${s.textHidden ? ' remote__toggle--on' : ''}`} aria-pressed={Boolean(s.textHidden)} onClick={() => void send({ action: 'textHidden', on: !s.textHidden })}>
          Ocultar letra
        </button>
        <button type="button" className="remote__toggle" title="Baja lo que se está mostrando" onClick={() => void send({ action: 'clear' })}>
          Pantalla vacía
        </button>
      </div>
      <div className="quick-grid">
        {QUICK.map((q) => (
          <button key={q.name} type="button" className="quick" disabled={busy !== null} onClick={() => void send({ action: 'quick', name: q.name })}>
            <strong>{q.label}</strong>
            <span>{q.hint}</span>
          </button>
        ))}
      </div>
      <h2 className="console__title">Programa</h2>
      <ol className="program">
        {p?.itemsList.map((item) => (
          <li key={item.position} className="program__item">
            <p className="program__head">
              <strong>{item.title}</strong>
            </p>
            <div className="program__slides">
              {item.slides.map((slide, i) => {
                const isLive = s.liveCursor?.item === item.position && s.liveCursor.slide === i;
                return (
                  <button key={i} type="button" className={`program__slide${isLive ? ' program__slide--live' : ''}`} aria-current={isLive ? 'true' : undefined} onClick={() => void send({ action: 'go', item: item.position, slide: i })}>
                    {slide.label ? <span className="program__label">{slide.label}</span> : null}
                    <span className="program__slide-text">{slide.text?.split('\n')[0] ?? slide.caption ?? item.resource?.name ?? item.title}</span>
                  </button>
                );
              })}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
